import {CopilotMetadata} from '@github-ui/copilot-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {addUrlToHistoryStack} from '@github-ui/history'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {AlertIcon} from '@primer/octicons-react'
import {Box, Button, Heading, Spinner} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import type React from 'react'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {useChat} from '../hooks/use-chat'
import {useGroupedMessages} from '../hooks/use-grouped-messages'
import {COPILOT_PATH, makeCAPIRequest, shuffle} from '../utils/copilot-chat-helpers'
import type {CopilotChatState} from '../utils/copilot-chat-reducer'
import {getActiveMessages} from '../utils/copilot-chat-subthreading-helpers'
import {type CopilotChatMessage, type FileReference, NullMessageId} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {getSelectedThread} from '../utils/get-selected-thread'
import {useDefaultModel} from '../utils/models'
import {suggestedPrompts} from '../utils/prompts'
import {AmbientErrorBanner} from './AmbientErrorBanner'
import styles from './Chat.module.css'
import {ChatInput} from './ChatInput'
import {ChatMessage} from './ChatMessage'
import {ChatMessagesGroup} from './ChatMessagesGroup'
import {ChatScrollContainer, useChatScroll} from './ChatScrollContainer'
import {Confirmation} from './Confirmation'
import CopilotSuggestions from './CopilotSuggestions'
import {LegalDisclaimer} from './LegalDisclaimer'
import {MenuPortalContainer, MessagesPortalContainer} from './PortalContainerUtils'
import {useEntitlement} from './quota/EntitlementContext'
import {QuotaExceededBanner} from './quota/QuotaExceededBanner'
import {QuotaExceededEmptyState} from './quota/QuotaExceededEmptyState'
import {QuotaMeterBanner} from './quota/QuotaMeterBanner'
import {TopicPicker} from './TopicPicker'

interface Suggestion {
  heading: string
  content: string
}

export const Messages = ({
  disableTopicPicker,
  inputRef,
  showQuotaExceededEmptyState,
}: {
  disableTopicPicker?: boolean
  inputRef: React.RefObject<HTMLTextAreaElement>
  showQuotaExceededEmptyState: boolean
}) => {
  const state = useChatState()
  const {isWaitingOnCopilot, showTopicPicker, streamingMessage} = state
  const {scrollToBottom} = useChatScroll()

  const {messages: activeMessages, groupedMessages} = useGroupedMessages(state)

  // Instant scroll to bottom on mount
  useLayoutEffect(() => scrollToBottom('instant'), [scrollToBottom])

  return (
    <>
      <h2 className="sr-only">Copilot Chat</h2>
      {showQuotaExceededEmptyState ? (
        <QuotaExceededEmptyState />
      ) : showTopicPicker && !disableTopicPicker ? (
        <TopicPicker />
      ) : (
        <div className={styles.ChatMainContainer}>
          <div className={clsx('copilot-messages-container', styles.messagesContainer)}>
            {activeMessages.length === 0 && (
              <p className={styles.legalText}>
                <LegalDisclaimer />
              </p>
            )}
            {groupedMessages.map((group, i) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <ChatMessagesGroup key={i} isLastGroup={i === groupedMessages.length - 1}>
                {group.map((message, j) => {
                  const isLatestMessage = i === groupedMessages.length - 1 && j === group.length - 1
                  const shouldShowMessage =
                    message === streamingMessage ||
                    message.id === NullMessageId ||
                    !isMessageEmpty(message) ||
                    !copilotFeatureFlags.dotcomChatClientSideSkills ||
                    message.error ||
                    shouldShowClientSkillMessage(message, isLatestMessage, state)
                  return (
                    shouldShowMessage && (
                      <ChatMessage
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={j}
                        isLatestMessage={isLatestMessage}
                        isFirstReply={activeMessages.length === 1}
                        isStreaming={message.id === streamingMessage?.id || (isLatestMessage && isWaitingOnCopilot)}
                        message={message}
                        inputRef={inputRef}
                      />
                    )
                  )
                })}
              </ChatMessagesGroup>
            ))}
            {state.clientSkillConfirmation && copilotFeatureFlags.dotcomChatClientSideSkills && (
              <div className="m-3">
                <Confirmation
                  confirmation={state.clientSkillConfirmation}
                  handleConfirmation={clientConfirmation =>
                    state.clientSkillConfirmation?.onSubmit?.(clientConfirmation.state === 'accepted')
                  }
                  isLatestMessage
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

const isMessageEmpty = (message: CopilotChatMessage) => {
  return (
    !message.content &&
    (!message.confirmations || message.confirmations.length === 0) &&
    (!message.clientConfirmations || message.clientConfirmations.length === 0)
  )
}

const isClientSkillMessage = (message: CopilotChatMessage) => {
  return (
    (message.clientSkillsRequests && message.clientSkillsRequests.length > 0) ||
    (message.clientToolResults && message.clientToolResults.length > 0)
  )
}

const shouldShowClientSkillMessage = (message: CopilotChatMessage, isLastMessage: boolean, state: CopilotChatState) => {
  return isClientSkillMessage(message) && isLastMessage && !state.streamingMessage && !state.clientSkillConfirmation
}

const Loading = () => {
  return (
    <div className={styles.loadingStateContainer}>
      <Spinner size="large" />
      <div className={styles.loadingText}>Loading conversation…</div>
    </div>
  )
}

const Error = () => {
  const {mode, reviewLab} = useChatState()
  return (
    <Box
      sx={{
        pl: mode === 'immersive' ? 3 : 2,
      }}
      className={styles.errorContainer}
    >
      <Octicon icon={AlertIcon} />
      Failed to load previous messages.
      {reviewLab && <>&nbsp;You are in a review lab. Please check that you are connected to the Developer VPN.</>}
    </Box>
  )
}

export const Chat = ({inputRef}: {inputRef?: React.RefObject<HTMLTextAreaElement>}) => {
  const state = useChatState()
  const manager = useChatManager()
  const {
    currentReferences,
    messagesLoading,
    messagesRestored,
    threadsLoading,
    currentTopic,
    messages,
    mode,
    selectedThreadID,
    showTopicPicker,
    context,
  } = state
  const thread = getSelectedThread(state)
  const suggestions = useMemo(() => getSuggestions(), [])
  const {textAreaRef} = useChat(inputRef)
  const {chatQuotaExceeded, isLicensedLimited, reloadQuota} = useEntitlement()
  const {ambientError} = useChatState()

  const [metadataLoading, setMetadataLoading] = useState(true)
  const [currentTab, setCurrentTab] = useState('chat')

  const copilotMetadata = isFeatureEnabled('copilot_metadata_poc')
  const isFileContext = !!context && context[0] && (context[0].type === 'file' || context[0].type === 'file-v2')
  const showChatTab = !copilotMetadata || (!!copilotMetadata && currentTab === 'chat')
  const defaultModel = useDefaultModel(state.availableModels)

  useEffect(() => {
    if (!selectedThreadID || mode === 'assistive') return

    const threadPath = `${COPILOT_PATH}/c/${selectedThreadID}`
    if (window.location.pathname === threadPath || messages.length === 0) return

    addUrlToHistoryStack(threadPath)
  }, [selectedThreadID, mode, messages.length])

  const handleUserSubmit = useCallback(
    async (content: string) => {
      const trimmedContent = content.trim()
      if (trimmedContent === '') return

      reloadQuota('user_submit')

      await manager.sendChatMessage({
        thread,
        content,
        references: currentReferences,
        topic: currentTopic,
        context,
        customInstructions: state.customInstructions,
        skillOptions: state.skillOptions,
        model: defaultModel,
      })
    },
    [
      reloadQuota,
      manager,
      thread,
      currentReferences,
      currentTopic,
      context,
      state.customInstructions,
      state.skillOptions,
      defaultModel,
    ],
  )

  const hasRestoredMessages = messagesRestored && messages.length > 0
  const isLoading =
    messagesLoading.state === 'loading' &&
    state.selectedThreadID &&
    !showTopicPicker &&
    // don't show the loading state if we have placeholder messages to show
    !hasRestoredMessages
  const isError = messagesLoading.state === 'error' || threadsLoading.state === 'error'
  const isLoaded = messagesLoading.state === 'loaded' || hasRestoredMessages
  const showQuotaExceededEmptyState = chatQuotaExceeded && (showTopicPicker || !thread)
  const shouldShowChatInput = !showTopicPicker && !showQuotaExceededEmptyState
  // We store this at the first render and don't change it because we don't want the suggested prompts to suddenly pop up
  // when the popover is dismissed. Instead we'll show prompts on subsequent loads if the popover is dismissed.
  const [renderedPopoverOnFirstLoad] = useState(state.renderAttachKnowledgeBaseHerePopover)

  const activeMessages = getActiveMessages(messages)

  // If there is a popover or a reference attached then we shouldn't show the suggested prompts
  const shouldShowSuggestedPrompts = Boolean(
    !isLoading &&
      !state.currentTopic &&
      !showTopicPicker &&
      activeMessages.length === 0 &&
      !renderedPopoverOnFirstLoad &&
      currentReferences.length === 0,
  )

  return (
    <>
      {copilotMetadata && isFileContext && (
        <CopilotMetadata
          fileContext={context[0] as FileReference}
          loading={metadataLoading}
          setLoading={setMetadataLoading}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          makeCAPIRequest={makeCAPIRequest}
        />
      )}
      {showChatTab && (
        <>
          <ChatScrollContainer
            className="copilot-chat-messages"
            style={{
              flex: '1 1 auto',
            }}
          >
            {isLoading && <Loading />}
            {isError && <Error />}
            {isLoaded && !isLoading && (
              <Messages inputRef={textAreaRef} showQuotaExceededEmptyState={showQuotaExceededEmptyState} />
            )}
            <MessagesPortalContainer />
          </ChatScrollContainer>
          {shouldShowSuggestedPrompts && (
            <div className={clsx('copilot-chat-messages', styles.suggestedPromptsGrid)}>
              {suggestions.map((s, i) => (
                <SuggestedPrompt
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={i}
                  heading={s.heading}
                  content={s.content}
                  onSubmit={() => {
                    void manager.sendChatMessage({
                      thread: getSelectedThread(state),
                      content: s.content,
                      references: state.currentReferences,
                      topic: state.currentTopic,
                      context: state.context,
                    })
                    sendEvent('copilot_chat_suggestion_click', {
                      topic: state.currentTopic?.name,
                    })
                  }}
                />
              ))}
            </div>
          )}
          {!isLoading && shouldShowChatInput && (
            <div className={clsx('copilot-chat-input copilot-chat-input-outer', styles.chatInputContainer)}>
              {chatQuotaExceeded ? (
                <QuotaExceededBanner />
              ) : (
                <>
                  {!shouldShowSuggestedPrompts && activeMessages.length === 0 && !state.showTopicPicker && (
                    <CopilotSuggestions suggestionKind="initial" />
                  )}
                  <div>
                    {isLicensedLimited && <QuotaMeterBanner />}
                    {ambientError && <AmbientErrorBanner ambientError={ambientError} />}
                    <ChatInput
                      key={selectedThreadID}
                      textAreaRef={textAreaRef}
                      onSubmit={handleUserSubmit}
                      isStreaming={!!state.streamingMessage}
                      size="small"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
      <MenuPortalContainer />
    </>
  )
}

function SuggestedPrompt({heading, content, onSubmit}: {heading: string; content: string; onSubmit: () => void}) {
  return (
    <Button onClick={onSubmit} className={styles.suggestedPromptButton}>
      <Heading as="h3" className={styles.promptHeading}>
        {heading}
      </Heading>
      <p className={styles.promptDescription}>{content}</p>
    </Button>
  )
}

function getSuggestions(): Suggestion[] {
  const categories = shuffle(Object.keys(suggestedPrompts)).slice(0, 4)
  return categories.map(c => ({
    heading: c,
    content: shuffle(suggestedPrompts[c]!)[0]!,
  }))
}

try{ Messages.displayName ||= 'Messages' } catch {}
try{ Loading.displayName ||= 'Loading' } catch {}
try{ Error.displayName ||= 'Error' } catch {}
try{ Chat.displayName ||= 'Chat' } catch {}
try{ SuggestedPrompt.displayName ||= 'SuggestedPrompt' } catch {}